import { buildOrderRequestBody, type OrderFormValues } from '@/components/santa-video/order-form-body'
import { retryAfterHeaderSeconds } from '@/components/santa-video/order-form-lifecycle'
import { checkoutAttributionField } from '@/lib/marketing-attribution'
import type { VideoLocale } from '@/server/domain/santa-video/locale'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

type CheckoutFailureReason = 'network_error' | 'invalid_response' | `http_${number}`

export type CheckoutResult = { checkoutUrl: string } | { message: string; retryAfterSeconds: number; reasonCode: CheckoutFailureReason }

/**
 * `POST /api/santa-video/orders` y su resultado, ya reducido a lo que la vista
 * necesita: o una `checkoutUrl` a la que navegar, o el `message` que enseñar
 * (el del servidor si lo hay, si no el genérico de red) con el `Retry-After`
 * de un 429 (0 en cualquier otro caso). Vive fuera del componente porque
 * separa la petición y su interpretación de la vista: el wizard sólo decide
 * qué hacer con el resultado.
 */
export async function requestCheckout(values: OrderFormValues, locale: VideoLocale, networkError: string, story: VideoStoryId): Promise<CheckoutResult> {
  try {
    const response = await fetch('/api/santa-video/orders', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...buildOrderRequestBody(values, locale, story), ...checkoutAttributionField() }),
    })
    const data = (await response.json().catch(() => null)) as { checkoutUrl?: string; error?: { message?: string } } | null
    if (response.status === 201 && data?.checkoutUrl) return { checkoutUrl: data.checkoutUrl }
    const retryAfterSeconds = response.status === 429 ? retryAfterHeaderSeconds(response) : 0
    return { message: data?.error?.message ?? networkError, retryAfterSeconds, reasonCode: checkoutFailureReason(response.status) }
  } catch {
    return { message: networkError, retryAfterSeconds: 0, reasonCode: 'network_error' }
  }
}

function checkoutFailureReason(status: number): CheckoutFailureReason {
  return status === 201 ? 'invalid_response' : `http_${status}`
}
